import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CloudSyncOutlined,
  FolderOpenOutlined,
  InboxOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Alert, Button, Form, Image, Input, Space, Upload, message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { clientImageAssetsService } from '@/services/clientImageAssetsService';
import type { DriveSyncResponse } from '@/services/clientImageAssetsService';
import { SectionPage } from './SectionPage';
import ImageAssetsLibraryPage from '@/pages/ImageAssetsLibraryPage';
import { COPY } from '@/lib/copyTokens';
import { useRegisterDirtyForm } from '@/components/settings/DirtyFormGuard';
import { isElevatedRole } from '@/lib/roles';
import type { TenantSettings } from '@/types/tenant';
import { apiClient } from '@/services/apiClient';
import { driveFolderUrl, parseDriveFolderId, splitDriveFolderValue } from '@/lib/driveFolder';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useSectionTenantSettings } from './useSectionTenantSettings';

const { Dragger } = Upload;

type MediaValues = Pick<TenantSettings, 'image_reference' | 'image_asset_folder_id'>;

/** The sync-slot renderings (design: ONE slot — states replace, never stack). */
type SyncErrorKind = 'not-shared' | 'flag-off' | 'timeout' | 'generic';

interface SyncError {
  kind: SyncErrorKind;
  detail: string;
}

/** Classify a failed sync call into its slot rendering. A timeout is NOT a
 * failure: the server run completes anyway and an idempotent re-run/refresh
 * shows the outcome. Server detail text renders verbatim. */
function classifySyncError(error: unknown): SyncError {
  const axiosError = error as AxiosError<{ detail?: unknown }>;
  if (axiosError?.code === 'ECONNABORTED') {
    return {
      kind: 'timeout',
      detail:
        'The sync is still running on the server — it was not interrupted. Refresh the library in a few minutes; running sync again is safe.',
    };
  }
  const status = axiosError?.response?.status;
  const detail = axiosError?.response?.data?.detail;
  const detailText = typeof detail === 'string' ? detail : '';
  if (status === 503) {
    return { kind: 'flag-off', detail: 'Drive sync is not enabled on this environment.' };
  }
  if (status === 502) {
    return { kind: 'not-shared', detail: detailText };
  }
  return { kind: 'generic', detail: detailText || 'Sync failed — running it again is safe.' };
}

/** SA-email mono chip with an azure Copy affordance — deliberately ONE
 * component so the empty-state instruction and the not-shared fix render the
 * same object. */
function SaEmailChip({ email }: { email: string | null | undefined }) {
  if (!email) {
    return <span className="text-sm text-ink-2">the platform service account</span>;
  }
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-teal-light px-1.5 py-0.5 align-middle">
      <span className="truncate font-mono text-xs text-teal-dark">{email}</span>
      <button
        type="button"
        className="shrink-0 text-xs font-medium text-coral hover:text-coral-hover"
        onClick={() => {
          navigator.clipboard.writeText(email);
          message.success('Copied');
        }}
      >
        Copy
      </button>
    </span>
  );
}

/** Head status chip — the card's one-glance answer. Dot colors: green/red are
 * status-only uses; the syncing dot is the brand ring accent (never a fill). */
function StatusChip({ state }: { state: 'connected' | 'not-connected' | 'syncing' | 'attention' }) {
  const dotClass = {
    connected: 'bg-[hsl(var(--status-success))]',
    'not-connected': 'bg-teal',
    syncing: 'bg-ring',
    attention: 'bg-destructive',
  }[state];
  const label = {
    connected: 'Connected',
    'not-connected': 'Not connected',
    syncing: 'Syncing…',
    attention: 'Needs attention',
  }[state];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 text-sm font-medium ${
        state === 'attention' ? 'text-destructive' : 'text-ink-2'
      }`}
    >
      <span aria-hidden className={`h-2 w-2 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

export default function MediaSection() {
  const [form] = Form.useForm<MediaValues>();
  const [isDirty, setIsDirty] = useState(false);
  const [isResolvingIcon, setIsResolvingIcon] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<DriveSyncResponse | null>(null);
  const [syncError, setSyncError] = useState<SyncError | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(false);
  const [libraryJump, setLibraryJump] = useState<{ state: string; nonce: number } | null>(null);
  const libraryRef = useRef<HTMLDivElement | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const { tenantId, tenantSettings, isLoading, isSaving, save } = useSectionTenantSettings();
  const userProfile = useWorkspaceStore((state) => state.userProfile);
  const isOperator = isElevatedRole(userProfile?.platform_role);
  const imageReference = Form.useWatch('image_reference', form);
  const primaryImage = imageReference || uploadedImages[0];
  const additionalImages = uploadedImages.filter((image) => image !== primaryImage);

  // The saved (server-confirmed) folder value — the sync reads THIS, not the
  // form field, so the button and read-back chip key off it.
  const savedFolder = tenantSettings?.image_asset_folder_id || '';

  const { data: driveSyncConfig } = useQuery({
    queryKey: ['driveSyncConfig', tenantId],
    queryFn: () => clientImageAssetsService.getDriveSyncConfig(tenantId!),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
  });
  const saEmail = driveSyncConfig?.service_account_email;

  useEffect(() => {
    form.setFieldsValue({
      image_reference: tenantSettings?.image_reference,
      image_asset_folder_id: tenantSettings?.image_asset_folder_id,
    });
    setUploadedImages(tenantSettings?.image_reference ? [tenantSettings.image_reference] : []);
    setIsDirty(false);
    setEditingFolder(false);
  }, [form, tenantSettings]);

  // Session sync state belongs to one tenant — never carry a receipt or an
  // error across a workspace switch.
  useEffect(() => {
    setSyncResult(null);
    setSyncError(null);
    setDetailsOpen(false);
    setLibraryJump(null);
  }, [tenantId]);

  const submit = useCallback(async () => {
    const values = await form.validateFields();
    // The operator may paste a share URL; the column stores '<folderId>' or
    // '<folderId>:<resourceKey>'. Validation already rejected anything
    // unparseable, so this cannot throw. When the folder field is hidden
    // (read-back chip mode) validateFields omits it — fall back to the saved
    // value so an image_reference save can never silently disconnect the
    // folder. Clearing sends '' (the PATCH route drops nulls, so undefined
    // would silently keep the old folder).
    const folderInput =
      values.image_asset_folder_id !== undefined
        ? values.image_asset_folder_id
        : tenantSettings?.image_asset_folder_id;
    const parsed = parseDriveFolderId(folderInput);
    const folderValue = parsed.ok ? parsed.stored : null;
    const succeeded = await save({
      image_reference: values.image_reference,
      image_asset_folder_id: folderValue ?? '',
    });
    if (succeeded) {
      // Reflect the normalised value back so the field shows what was stored.
      form.setFieldsValue({ image_asset_folder_id: folderValue ?? undefined });
      setIsDirty(false);
      setEditingFolder(false);
    }
    return succeeded;
  }, [form, save, tenantSettings]);

  const discard = useCallback(() => {
    form.setFieldsValue({
      image_reference: tenantSettings?.image_reference,
      image_asset_folder_id: tenantSettings?.image_asset_folder_id,
    });
    setUploadedImages(tenantSettings?.image_reference ? [tenantSettings.image_reference] : []);
    setIsDirty(false);
    setEditingFolder(false);
  }, [form, tenantSettings]);

  useRegisterDirtyForm(
    useMemo(
      () => ({ isDirty: () => isDirty, save: submit, discard }),
      [discard, isDirty, submit],
    ),
  );

  useEffect(() => {
    if (imageReference) {
      setUploadedImages((images) =>
        images.includes(imageReference) ? images : [imageReference, ...images],
      );
    }
  }, [imageReference]);

  if (!tenantId) {
    return (
      <SectionPage sectionId="media">
        <p className="text-sm text-ink-2">{COPY.chooseEntity}</p>
      </SectionPage>
    );
  }

  const uploadImage = async (file: File) => {
    const data = new FormData();
    data.append('file', file);
    data.append('website', tenantId);
    try {
      const response = await apiClient.post<{ success: boolean; url?: string }>(
        '/tenant-settings/upload-image',
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (!response.data.success || !response.data.url) throw new Error('Image upload failed');
      form.setFieldsValue({ image_reference: response.data.url });
      message.success(COPY.sections.mediaInfo.imageUploadSucceeded);
    } catch (error) {
      console.error('Failed to upload image', error);
      message.error(COPY.saveFailed);
    }
    return false;
  };

  const findBrandIcon = async () => {
    setIsResolvingIcon(true);
    try {
      const result = await clientImageAssetsService.resolveBrandImage(tenantId);
      if (result.resolved && result.url) {
        form.setFieldsValue({ image_reference: result.url });
        setIsDirty(true);
        message.success(COPY.sections.mediaInfo.brandIconFound);
      } else {
        // No-guess: nothing was changed server-side — keep the empty state.
        message.info(COPY.sections.mediaInfo.brandIconNotFound);
      }
    } catch (error) {
      console.error('Failed to resolve brand icon', error);
      message.error(COPY.sections.mediaInfo.brandIconFailed);
    } finally {
      setIsResolvingIcon(false);
    }
  };

  const runDriveSync = async () => {
    setIsSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    setDetailsOpen(false);
    try {
      const result = await clientImageAssetsService.syncFromDrive(tenantId);
      setSyncResult(result);
    } catch (error) {
      console.error('Drive sync failed', error);
      setSyncError(classifySyncError(error));
    } finally {
      setIsSyncing(false);
    }
  };

  const jumpToReviewQueue = () => {
    setLibraryJump({ state: 'needs-review', nonce: Date.now() });
    libraryRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  // ----- Drive strip derived state (one state, three renderings: head chip,
  // button, slot — they must always agree) -----
  const showFolderInput = editingFolder || !savedFolder;
  const isPartial = Boolean(syncResult && (syncResult.cap_hit || syncResult.failed > 0));
  const chipState: 'connected' | 'not-connected' | 'syncing' | 'attention' = isSyncing
    ? 'syncing'
    : syncError?.kind === 'not-shared'
      ? 'attention'
      : savedFolder
        ? 'connected'
        : 'not-connected';
  const syncDisabledReason = !savedFolder
    ? 'Save a Drive folder first — sync reads the saved value.'
    : isDirty || editingFolder
      ? 'Save your changes first — sync reads the saved value.'
      : driveSyncConfig && !driveSyncConfig.enabled
        ? 'Drive sync is not enabled on this environment.'
        : null;
  const syncButtonLabel = isSyncing ? 'Syncing…' : isPartial ? 'Sync again' : 'Sync from Drive';
  const hasReceiptDetails = Boolean(
    syncResult && (syncResult.failures.length > 0 || syncResult.unsupported.length > 0),
  );

  return (
    <SectionPage sectionId="media">
      <section aria-labelledby="brand-image-heading" className="max-w-4xl space-y-4">
        <h3 id="brand-image-heading" className="text-sm font-semibold tracking-tight text-ink">
          {COPY.sections.mediaInfo.brandImage}
        </h3>
        <Form<MediaValues>
          form={form}
          layout="vertical"
          onFinish={submit}
          onValuesChange={() => setIsDirty(true)}
        >
          <Form.Item
            name="image_reference"
            label={COPY.sections.mediaInfo.primaryBrandImage}
            extra={COPY.sections.mediaInfo.primaryBrandImageHelp}
          >
            <Space direction="vertical" className="w-full">
              {primaryImage ? (
                <Image.PreviewGroup>
                  <Image
                    src={primaryImage}
                    alt={COPY.sections.mediaInfo.primaryReferenceAlt}
                    height={200}
                    className="object-cover"
                  />
                </Image.PreviewGroup>
              ) : (
                <Alert message={COPY.sections.mediaInfo.noImageSelected} type="info" showIcon />
              )}
              <Input className="w-full" placeholder={COPY.sections.mediaInfo.imageUrlPlaceholder} />
              <Button
                icon={<SearchOutlined />}
                loading={isResolvingIcon}
                onClick={findBrandIcon}
              >
                {COPY.sections.mediaInfo.findBrandIcon}
              </Button>
            </Space>
          </Form.Item>
          <Dragger
            name="image_reference_upload"
            multiple={false}
            showUploadList={false}
            accept="image/*"
            beforeUpload={uploadImage}
            className="border-border bg-card"
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{COPY.sections.mediaInfo.imageUploadAction}</p>
            <p className="ant-upload-hint">{COPY.sections.mediaInfo.imageUploadHelp}</p>
          </Dragger>
          {additionalImages.length > 0 ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-ink-2">{COPY.sections.mediaInfo.additionalReferences}</p>
              <Image.PreviewGroup>
                <Space wrap>
                  {additionalImages.map((source) => (
                    <Image
                      key={source}
                      src={source}
                      alt={COPY.sections.mediaInfo.additionalReferenceAlt}
                      width={120}
                      height={120}
                      className="cursor-pointer object-cover"
                      onClick={() => form.setFieldsValue({ image_reference: source })}
                    />
                  ))}
                </Space>
              </Image.PreviewGroup>
            </div>
          ) : null}

          {/* Drive sync strip — ONE card, ONE status slot (design pass).
              States replace each other; the head chip, the button, and the
              slot always agree. */}
          <section
            aria-labelledby="asset-source-heading"
            className="mt-8 space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm"
          >
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-coral-light text-coral-dark">
                <FolderOpenOutlined aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3
                  id="asset-source-heading"
                  className="text-sm font-semibold tracking-tight text-ink"
                >
                  Image asset source
                </h3>
                <p className="text-sm text-ink-2">
                  Google Drive folder holding this workspace&rsquo;s own photos and logos
                </p>
              </div>
              <StatusChip state={chipState} />
            </div>

            {/* Folder row: read-back chip (what is SAVED) or the input. The
                Form.Item stays mounted (hidden) in chip mode so the antd form
                keeps owning the value. */}
            <div className={showFolderInput ? 'flex items-start gap-2' : 'hidden'}>
              <Form.Item
                name="image_asset_folder_id"
                className="mb-0 flex-1"
                extra="Paste the folder’s share link, or its folder ID. Leave empty to disconnect — clearing only stops future syncs; images already in the library stay."
                validateFirst
                rules={[
                  {
                    validator: async (_rule, value) => {
                      const parsed = parseDriveFolderId(value);
                      if (!parsed.ok) throw new Error(parsed.reason);
                    },
                  },
                ]}
              >
                <Input
                  className="w-full"
                  allowClear
                  placeholder="https://drive.google.com/drive/folders/..."
                />
              </Form.Item>
              <Button onClick={() => form.submit()} loading={isSaving}>
                Save
              </Button>
            </div>
            {!showFolderInput ? (
              <div className="flex items-center gap-2">
                <div
                  className={`flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg border bg-teal-light px-3 py-1.5 ${
                    syncError?.kind === 'not-shared' ? 'border-destructive/40' : 'border-border'
                  }`}
                >
                  <span className="truncate font-mono text-xs text-ink">
                    {splitDriveFolderValue(savedFolder).folderId}
                  </span>
                  <a
                    className="shrink-0 text-sm font-medium text-coral hover:text-coral-hover"
                    href={driveFolderUrl(savedFolder)}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Open in Drive ↗
                  </a>
                </div>
                <Button disabled={isSyncing} onClick={() => setEditingFolder(true)}>
                  Change
                </Button>
              </div>
            ) : null}

            {/* Share-instruction line — hidden mid-sync (not actionable). */}
            {!isSyncing ? (
              showFolderInput ? (
                <div className="space-y-1">
                  <p className="text-sm text-ink-2">
                    First, share the folder with <SaEmailChip email={saEmail} /> as Viewer —
                    then paste its link above.
                  </p>
                  <p className="text-sm text-ink-2">
                    Generated content falls back to the other configured image sources while no
                    folder is connected.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-ink-2">
                  Shared with <SaEmailChip email={saEmail} /> as Viewer
                </p>
              )
            ) : null}

            {isOperator ? (
              <>
                <div className="border-t border-border" />

                {/* Sync row: the button and its inline line (disabled reason /
                    duration note / verbatim partial message / last-sync). */}
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="primary"
                    icon={isSyncing ? undefined : <CloudSyncOutlined />}
                    loading={isSyncing}
                    disabled={Boolean(syncDisabledReason)}
                    onClick={runDriveSync}
                  >
                    {syncButtonLabel}
                  </Button>
                  {isSyncing ? (
                    <span className="text-sm text-ink-2">
                      A whole-folder sync can take several minutes. Leaving this page won&rsquo;t
                      interrupt it.
                    </span>
                  ) : syncDisabledReason ? (
                    <span className="text-sm text-ink-2">{syncDisabledReason}</span>
                  ) : isPartial && syncResult ? (
                    <span className="text-sm text-ink-2">
                      {syncResult.message}. Already-synced images are skipped.
                    </span>
                  ) : syncResult ? (
                    <span className="text-sm text-ink-2">Last sync just now</span>
                  ) : syncError ? (
                    <span className="text-sm text-ink-2">Last attempt just now</span>
                  ) : null}
                </div>

                {/* THE status slot — exactly one of: error panel | receipt. */}
                {syncError ? (
                  syncError.kind === 'not-shared' ? (
                    <div className="space-y-1 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                      <p>
                        <strong>Google Drive can&rsquo;t read this folder.</strong> It isn&rsquo;t
                        shared with the service account (or the share was revoked). Share it with{' '}
                        <SaEmailChip email={saEmail} /> as <strong>Viewer</strong>, then sync
                        again.
                      </p>
                      {syncError.detail ? (
                        <p className="font-mono text-xs">{syncError.detail}</p>
                      ) : null}
                    </div>
                  ) : syncError.kind === 'timeout' ? (
                    <div className="rounded-lg border border-border bg-coral-light px-3 py-2 text-sm text-coral-dark">
                      {syncError.detail}
                    </div>
                  ) : syncError.kind === 'flag-off' ? (
                    <div className="rounded-lg border border-border bg-teal-light px-3 py-2 text-sm text-teal-dark">
                      {syncError.detail}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                      {syncError.detail}
                    </div>
                  )
                ) : syncResult ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-teal-light px-3 py-2 text-sm text-ink">
                      <span>
                        <strong className="text-[hsl(var(--status-success))]">
                          {syncResult.ingested}
                        </strong>{' '}
                        added to review queue
                      </span>
                      <span>
                        <strong>{syncResult.skipped_duplicates}</strong> already in library
                      </span>
                      <span>
                        <strong>{syncResult.skipped_unsupported}</strong> unsupported
                      </span>
                      {syncResult.failed > 0 ? (
                        <span className="text-destructive">
                          <strong>{syncResult.failed}</strong> failed
                        </span>
                      ) : null}
                      {syncResult.remaining > 0 ? (
                        <span>
                          <strong>{syncResult.remaining}</strong> remaining
                        </span>
                      ) : null}
                      <span className="ml-auto">
                        {isPartial && hasReceiptDetails ? (
                          <button
                            type="button"
                            className="text-sm font-medium text-coral hover:text-coral-hover"
                            aria-expanded={detailsOpen}
                            onClick={() => setDetailsOpen((open) => !open)}
                          >
                            Details
                          </button>
                        ) : syncResult.ingested > 0 ? (
                          <button
                            type="button"
                            className="text-sm font-medium text-coral hover:text-coral-hover"
                            onClick={jumpToReviewQueue}
                          >
                            Review {syncResult.ingested} new →
                          </button>
                        ) : null}
                      </span>
                    </div>
                    {!isPartial && hasReceiptDetails ? (
                      <button
                        type="button"
                        className="text-sm font-medium text-coral hover:text-coral-hover"
                        aria-expanded={detailsOpen}
                        onClick={() => setDetailsOpen((open) => !open)}
                      >
                        Details
                      </button>
                    ) : null}
                    {detailsOpen && hasReceiptDetails ? (
                      <div className="space-y-1 rounded-lg border border-border px-3 py-2 text-sm text-ink-2">
                        {syncResult.failures.map((failure) => (
                          <p key={failure.file_id}>
                            <span className="text-destructive">{failure.name}</span>:{' '}
                            {failure.reason}
                          </p>
                        ))}
                        {syncResult.unsupported.length > 0 ? (
                          <p>
                            Unsupported:{' '}
                            {syncResult.unsupported.map((entry) => entry.name).join(', ')}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}
          </section>

          <Button className="mt-4" type="primary" htmlType="submit" loading={isLoading || isSaving}>
            {COPY.cards.dirtyGuard.save}
          </Button>
        </Form>
      </section>
      <div className="mt-8" ref={libraryRef}>
        <ImageAssetsLibraryPage filterJump={libraryJump} />
      </div>
    </SectionPage>
  );
}
